// ── KpiCard — RR-BI-1 KPI-AFFORDANCE (shared by the Vendor + Org Risk Responses operations workspaces) ──
//
// A KPI tile. Some tiles drill through to a filtered register (they carry an onClick); others are read-only
// (no onClick). ⚠ The clickable ones must LOOK clickable so the affordance isn't hidden — but the signal is
// derived from the REAL handler wiring, never a hardcoded guess: `clickable = !!onClick`. A tile with the
// affordance but no handler would be a lie; a wired tile without it leaves the gap. Both are prevented by
// keying every visual on `onClick` presence.
//
// ⚠ BEHAVIOR-FROZEN: this is purely visual signalling of what is already true. onClick still fires the exact
// same drill-through (a FILTER, never a text search). No keyboard/role semantics are added (that would be new
// behavior) — the tile stays the plain div-onClick it has always been; only the visual affordance changes.

import { ChevronRight } from 'lucide-react';
import { InfoTooltip } from '@/components/shared/InfoTooltip';
import { cn } from '@/lib/utils';

export interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  info: string;
  onClick?: () => void;
}

export function KpiCard({ label, value, sub, info, onClick }: KpiCardProps) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      // ⚠ RR-UX-1 S2 (item 5): clickable tiles are real controls — role=button + tabIndex + Enter/Space fire
      // the SAME onClick (preventDefault Space to stop page scroll). ⚠ div-with-role, NOT a native <button>:
      // KpiCard nests an InfoTooltip (interactive) and a native button can't contain another. Info tiles stay
      // inert (no role/tabindex/keydown). Behavior-frozen: onClick is unchanged — this only adds keyboard+a11y.
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }
        : undefined}
      data-clickable={clickable ? 'true' : 'false'}
      className={cn(
        'group relative rounded-lg border bg-bg-surface p-3.5 flex flex-col gap-1 transition-colors',
        clickable
          // Persistent affordance at REST (border tint) that strengthens on hover; the chevron below is the
          // explicit "drill in" cue + a focus-visible ring for keyboard users. Info-only tiles read neutral.
          ? 'cursor-pointer border-accent-cyan/25 hover:border-accent-cyan/70 hover:bg-bg-surface-2/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan/60'
          : 'cursor-default border-border-subtle',
      )}
    >
      {clickable && (
        <ChevronRight
          aria-hidden
          className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 text-text-faint transition-colors group-hover:text-accent-cyan"
        />
      )}
      <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary inline-flex items-center gap-1">
        {label}<InfoTooltip text={info} />
      </span>
      <span className="text-2xl font-bold text-text-primary tabular-nums">{value}</span>
      {sub && <span className="text-[11px] text-text-secondary">{sub}</span>}
    </div>
  );
}
